import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import Header from '../components/Header';
import { useAuth } from '../contexts/authContext';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../utils/firebase';

const FAQ = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [openItems, setOpenItems] = useState({});

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserRole(userDoc.data().role || 'host');
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          setUserRole('host');
        }
      }
    };
    fetchUserRole();
  }, [user]);

  const handleBackToDashboard = () => {
    if (userRole === 'admin') {
      navigate('/admin');
    } else if (userRole === 'team_captain' || userRole === 'team_member') {
      navigate('/team/dashboard');
    } else {
      navigate('/home');
    }
  };

  const categories = useMemo(() => [
    { id: 'getting-started', name: 'Getting Started', icon: '🚀' },
    { id: 'pricing-model', name: 'Pricing & Game Launches', icon: '💰' },
    { id: 'marketplace', name: 'Marketplace', icon: '🏪' },
    { id: 'game-creation', name: 'Game Creation', icon: '🎮' },
    { id: 'revenue-sharing', name: 'Revenue Sharing', icon: '💸' },
    { id: 'analytics', name: 'Analytics & Tracking', icon: '📊' },
    { id: 'teams', name: 'Teams & Competition', icon: '🏆' },
    { id: 'security-privacy', name: 'Security & Privacy', icon: '🔒' },
    { id: 'venue-features', name: 'Venue Features', icon: '🏢' },
    { id: 'technical', name: 'Technical Support', icon: '🔧' }
  ], []);

  // Handle URL hash for direct linking to FAQ sections
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && categories.find(cat => cat.id === hash)) {
      setActiveCategory(hash);
    }
  }, [categories]);

  const toggleItem = (itemId) => {
    setOpenItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    // Update URL hash for direct linking
    window.location.hash = categoryId;
  };

  const faqData = {
    'getting-started': [
      {
        id: 'what-is-triviaplug',
        question: 'What is Trivia Plug?',
        answer: `Trivia Plug is a comprehensive professional trivia hosting software platform that allows you to:
• Create and host trivia games with professional tools
• Buy games from other creators in the marketplace
• Access 365 official Trivia Plug games per year
• Compete with teams and track performance
• Earn money by selling your games (50% revenue share)
• Launch games with pay-per-use pricing ($15 per launch for created games)
• Access free analytics and league integration

The platform combines game creation, marketplace commerce, team competition, and revenue sharing in one ecosystem designed for professional trivia hosts.`
      },
      {
        id: 'how-to-signup',
        question: 'How do I get started?',
        answer: `1. **Sign up** for a free account
2. **Create your profile** and team (optional)
3. **Purchase games** from the marketplace
4. **Create games** for personal use or marketplace submission
5. **Buy games** from the marketplace or official Trivia Plug games
6. **Launch games** and host trivia events (pricing varies by user type)
7. **Access analytics** and track performance

Start by exploring the Host Dashboard to see all available features.`
      },
      {
        id: 'account-types',
        question: 'What types of accounts are there?',
        answer: `**Host Account:** Basic account for hosting games
• Create personal games
• Buy games from marketplace
• Launch games (pay-per-use: $15 per launch for created games)
• Join/create teams
• Access free analytics

**Creator Account:** For selling games
• All Host features
• Submit games to marketplace
• Earn 50% revenue from sales
• Access creator analytics
• Build reputation

**Admin Account:** Platform management
• Review marketplace submissions
• Manage users and content
• Access platform analytics
• Monitor security and abuse prevention

**Venue Account:** For venue management
• Revenue self-reporting
• Analytics and performance tracking
• Host coordination tools`
      }
    ],
    'pricing-model': [
      {
        id: 'game-launch-pricing',
        question: 'How does pay-per-use game launch pricing work?',
        answer: `**Pay-Per-Use Launch Pricing:**
• Created game launches: $15 per launch (one-time payment)
• Marketplace game purchases: Base price (min $20) + $15 launch fee
• No subscriptions, no monthly limits
• Session expires after 12 hours OR when final scores submitted
• Single session per host prevents account sharing

**What's Included:**
• Full game access for 12 hours
• Professional hosting tools
• Analytics and tracking
• Team management features
• League integration (official games only)

**Session Management:**
• Game sessions are protected from sharing
• Single active game per host
• Automatic session termination after 12 hours
• Anti-abuse protection prevents multiple venue usage

**Pricing Examples:**
• Launching your own created game: $15 per launch
• Marketplace game ($20 base): $35 total ($20 + $15 launch fee)
• Marketplace game ($50 base): $65 total ($50 + $15 launch fee)
• To launch again: Must repurchase (marketplace) or pay $15 again (created games)`
      },
      {
        id: 'game-session-limits',
        question: 'What are the game session limits and rules?',
        answer: `**12-Hour Game Window:**
• All games have 12-hour access window
• Session starts when game is launched
• Expires when final scores submitted OR 12 hours pass
• Prevents game sharing between hosts

**Single Session Protection:**
• Only 1 active game per host
• Prevents running games at multiple venues simultaneously
• Protects against account sharing abuse
• Ensures fair pricing model

**Session Security:**
• Device fingerprinting prevents sharing
• IP address monitoring
• Automatic abuse detection
• Real-time security monitoring

**Legitimate Use:**
• Single venue hosts: No impact
• Sequential venue hosting: Supported
• Multiple games per day: Supported (one at a time)
• Team events: Full support`
      },
      {
        id: 'official-games',
        question: 'What are Official Trivia Plug Games?',
        answer: `**Official Games:**
• 365 premium games per year (1 per day)
• $35 per game (includes launch right)
• League score integration included
• Premium quality and content
• No revenue sharing (100% Trivia Plug owned)

**League Integration:**
• Free league integration for official games only
• League scores only recorded for official games
• Maintains competitive integrity
• Drives sales of official games
• Attracts players with league benefits

**Quality Features:**
• Professional content creation
• Consistent difficulty levels
• Engaging questions and categories
• Regular updates and improvements
• Comprehensive answer explanations

**Benefits:**
• Guaranteed quality content
• League competition access
• No content creation required
• Reliable game availability
• Professional hosting experience`
      },
      {
        id: 'marketplace-pricing',
        question: 'How does marketplace pricing work?',
        answer: `**Marketplace Game Pricing:**
• Base price (set by creator, min $20) includes 1 launch right
• 12-hour access window for rented games
• 50% revenue sharing with game creators
• Automatic access revocation after 12 hours

**Revenue Split:**
• Creator earns 50% of rental fee
• Trivia Plug earns 50% of rental fee
• Launch right included with purchase
• Transparent pricing and tracking

**Example Transaction:**
• Base price: $20 (includes 1 launch right)
• Total cost: $20
• Creator earns: $10 (50% of base price)
• Trivia Plug earns: $10 (50% of base price)

**Access Rules:**
• 12-hour window from purchase
• Single session per purchase
• No sharing between hosts
• Automatic expiration protection`
      }
    ],
    'marketplace': [
      {
        id: 'what-is-marketplace',
        question: 'What is the Trivia Plug Marketplace?',
        answer: `The marketplace is where creators sell their games and hosts buy games to use.

**For Buyers:**
• Browse quality trivia games
• Rent games for 12 hours
• Pay base price (includes 1 launch right)
• Access professional hosting tools

**For Creators:**
• Submit games for approval
• Earn 50% revenue from rentals
• Build reputation and visibility
• Create passive income stream

**Quality Control:**
• All games reviewed before approval
• Quality creators promoted more
• No spam or duplicate submissions
• Maintains platform standards

**Game Ownership:**
• Trivia Plug owns all marketplace games
• Creators retain 50% revenue rights
• Games cannot be deleted once approved
• Platform ensures quality and consistency`
      },
      {
        id: 'how-to-buy-games',
        question: 'How do I buy games from the marketplace?',
        answer: `1. **Browse Marketplace**
   • Filter by category, price, rating
   • Read game descriptions and reviews
   • Check creator reputation

2. **Purchase Game**
   • Click "Buy Game" button
   • Pay rental fee (varies by game)
   • Game added to your library

3. **Launch Game**
   • Go to "My Games" section
   • Click "Launch Game"
   • Use included launch right (12-hour session)
   • Complete game within time limit

**Important:** You're renting games for 12 hours, not buying them forever. Each purchase includes one launch right.`
      },
      {
        id: 'marketplace-ownership',
        question: 'Do I own the games I buy from the marketplace?',
        answer: `**No, you don't own marketplace games.**

**Marketplace Games:**
• Trivia Plug owns all marketplace games
• You rent them for 12 hours
• Pay base price (includes 1 launch right)
• Must complete within time limit

**Personal Games:**
• You own your personal games
• Can use anytime (pricing varies by user type)
• Not available in marketplace until approved
• Stay private to you

**Official Games:**
• Trivia Plug owns official games
• $20 per game (includes launch right)
• League integration included
• Premium quality content

**Key Difference:**
Marketplace = rental model, Personal = ownership model, Official = premium model`
      },
      {
        id: 'marketplace-approval',
        question: 'How does marketplace approval work?',
        answer: `**Submission Process:**
1. Create game for personal use
2. Submit to marketplace for review
3. Quality and accuracy review
4. Approval or rejection with feedback
5. Approved games appear in marketplace

**Review Criteria:**
• Content quality and accuracy
• Game structure and flow
• Appropriate difficulty level
• Original content (no duplicates)
• Platform guidelines compliance

**Timeline:**
• Reviews not instant
• Varies based on submission queue
• Quality creators prioritized
• Multiple submissions of same game = suspension

**Quality Benefits:**
• Good creators promoted more
• Higher visibility in marketplace
• Increased sales potential
• Better reputation building

**Ownership Transfer:**
• Once approved, Trivia Plug owns the game
• Creator retains 50% revenue rights
• Game cannot be deleted or modified
• Platform ensures quality maintenance`
      }
    ],
    'game-creation': [
      {
        id: 'how-to-create-game',
        question: 'How do I create a trivia game?',
        answer: `1. **Access Game Creator**
   • Go to "Create Game" page
   • Fill in basic game information
   • Choose categories and difficulty

2. **Add Game Content**
   • Create 4 quarters (8 questions each)
   • Add halftime question
   • Create final question
   • Add tiebreaker question
   • Include images (optional)

3. **Review and Submit**
   • Preview your game
   • Test all questions and answers
   • Submit for personal use or marketplace

**Game Structure:**
• Quarter 1-4: 8 questions each
• Halftime: 1 special question
• Final: 1 high-value question
• Tiebreaker: 1 question for ties

**Tips:**
• Use clear, accurate questions
• Include diverse categories
• Test difficulty balance
• Add engaging images`
      },
      {
        id: 'personal-vs-marketplace',
        question: 'What\'s the difference between personal and marketplace games?',
        answer: `**Personal Games:**
• You own the game completely
• Use anytime (pricing varies by user type)
• Private to you only
• No revenue sharing
• Can submit to marketplace later

**Marketplace Games:**
• Trivia Plug owns the game
• Available for others to rent
• 50/50 revenue sharing with you
• Quality review required
• Cannot be deleted once approved

**Submission Process:**
• Start as personal game
• Test and refine
• Submit for marketplace review
• Wait for approval
• Appear in marketplace if approved

**Revenue:**
• Personal games = no revenue
• Marketplace games = 50% of rental fees

**Launch Fees:**
• Launch fees vary by user type and game source
• Launch fees go to Trivia Plug
• Revenue sharing only on rental fees`
      },
      {
        id: 'game-quality',
        question: 'How do I create high-quality games?',
        answer: `**Content Quality:**
• Accurate, well-researched questions
• Clear, unambiguous answers
• Appropriate difficulty progression
• Diverse category coverage
• Engaging and interesting content

**Structure:**
• Logical question flow
• Balanced difficulty across quarters
• Meaningful halftime and final questions
• Effective tiebreaker question
• Proper scoring system

**Presentation:**
• High-quality images
• Clear formatting
• Professional appearance
• Consistent style
• Engaging descriptions

**Testing:**
• Play through entire game
• Verify all answers correct
• Check difficulty balance
• Test with others
• Refine based on feedback

**Marketplace Success:**
• Quality games approved faster
• Better ratings and reviews
• Higher sales potential
• Improved creator reputation`
      }
    ],
    'revenue-sharing': [
      {
        id: 'how-revenue-works',
        question: 'How does revenue sharing work?',
        answer: `**Simple 50/50 Split on Base Price:**
When someone rents your game, you get exactly 50% of the base price (not including the $15 launch fee).

**Examples:**
• $20 base price ($35 total) → You earn $10 (50% of $20 base) + $15 launch fee to TriviaPlug
• $30 base price ($45 total) → You earn $15 (50% of $30 base) + $15 launch fee to TriviaPlug
• $40 base price ($55 total) → You earn $20 (50% of $40 base) + $15 launch fee to TriviaPlug

**Launch Fees:**
Launch fees ($15) go entirely to Trivia Plug and are not shared.

**Example Transaction:**
• Base price: $20
• Launch fee: $15
• Total buyer pays: $35
• You earn: $10 (50% of $20 base)
• Trivia Plug earns: $25 ($10 base + $15 launch)

**Instant Payments:**
• Money appears in your account immediately
• No waiting periods
• Transparent transaction tracking
• Full payment history available

**Revenue Tracking:**
• Real-time earnings updates
• Detailed transaction logs
• Performance analytics
• Tax reporting support`
      },
      {
        id: 'revenue-vs-launch-fees',
        question: 'What\'s the difference between revenue sharing and launch fees?',
        answer: `**Revenue Sharing (Game Rentals):**
• You earn 50% of rental fees
• One-time payment per rental
• Based on game price you set
• Goes to creators and Trivia Plug

**Launch Rights (Software Usage):**
• Included with marketplace purchases (1 launch per purchase)
• Created game launches cost $15 per launch (no subscriptions)
• Covers platform maintenance and tools

**Example Scenario:**
1. Someone purchases your $50 game ($65 total: $50 base + $15 launch fee) → You get $25 (50% of base price)
2. They launch the game → Launch right is consumed (included with purchase)
3. They complete game within 12 hours
4. To launch again → They must repurchase the game

**Key Point:**
You earn from game rentals (50% of base price), not from launch fees. Launch rights are included with marketplace purchases.`
      },
      {
        id: 'revenue-tracking',
        question: 'How do I track my revenue and earnings?',
        answer: `**Creator Dashboard:**
• Total earnings overview
• Sales count and analytics
• Top performing games
• Revenue trends over time

**Transaction Details:**
• Game name and buyer
• Payment method used
• Actual amount paid
• Your share (50%)
• Timestamp and status

**Stripe Integration:**
• Automatic payment processing
• Real-time revenue tracking
• Direct payout capabilities
• Transaction history

**Analytics Features:**
• Earnings by game
• Sales by time period
• Buyer demographics
• Performance metrics

**Access:**
• Creator Dashboard → Earnings tab
• Detailed transaction logs
• Export capabilities
• Real-time updates`
      }
    ],
    'analytics': [
      {
        id: 'what-analytics',
        question: 'What analytics are available?',
        answer: `**Creator Analytics:**
• Game sales and revenue
• Performance by game
• Buyer demographics
• Earnings trends
• Marketplace visibility

**Host Analytics:**
• Games launched
• Launch usage tracking
• Team performance
• Game completion rates
• Spending patterns

**Team Analytics:**
• Team performance scores
• Individual member stats
• Competition rankings
• Historical performance
• Achievement tracking

**Venue Analytics:**
• Revenue tracking
• Player counts
• Game performance
• Host performance
• Business insights

**Platform Analytics:**
• Overall usage statistics
• Popular categories
• Market trends
• Quality metrics
• User engagement

**Real-time Tracking:**
• Live updates
• Instant notifications
• Performance alerts
• Trend analysis`
      },
      {
        id: 'how-analytics-work',
        question: 'How are analytics tracked and calculated?',
        answer: `**Data Collection:**
• Automatic tracking of all actions
• Real-time data capture
• Secure data storage
• Privacy-compliant collection

**Revenue Analytics:**
• Sales transactions
• Payment processing
• Revenue sharing calculations
• Launch usage tracking
• Performance metrics

**Game Analytics:**
• Launch frequency
• Completion rates
• User engagement
• Rating and reviews
• Marketplace performance

**Team Analytics:**
• Competition participation
• Score tracking
• Performance rankings
• Achievement unlocks
• Historical data

**Privacy Protection:**
• Personal data anonymized
• Secure data transmission
• User consent required
• GDPR compliance`
      },
      {
        id: 'analytics-access',
        question: 'How do I access my analytics?',
        answer: `**Creator Dashboard:**
• Go to Creator Dashboard
• Click "Earnings" tab
• View revenue analytics
• Access sales data

**Host Dashboard:**
• View usage statistics
• Track launch usage
• Monitor game launches
• Check team performance

**Team Analytics:**
• Team Dashboard
• Performance tracking
• Competition results
• Achievement progress

**Venue Analytics:**
• Venue Dashboard
• Revenue tracking
• Player analytics
• Performance metrics

**Export Options:**
• Download reports
• CSV data export
• PDF summaries
• Custom date ranges

**Real-time Updates:**
• Live dashboard updates
• Instant notifications
• Performance alerts
• Trend analysis`
      }
    ],
    'teams': [
      {
        id: 'what-are-teams',
        question: 'What are teams and how do they work?',
        answer: `**Team Structure:**
• Groups of players competing together
• Captain manages team
• Members contribute to team score
• Compete against other teams

**Team Features:**
• Team profile and branding
• Member management
• Performance tracking
• Competition participation
• Achievement system

**Team Roles:**
• Captain: Team leader and manager
• Members: Active participants
• Recruits: Potential new members

**Competition:**
• Team vs team matches
• Leaderboard rankings
• Tournament participation
• Achievement unlocks
• Performance tracking

**Benefits:**
• Collaborative gameplay
• Social interaction
• Competitive environment
• Achievement sharing
• Team pride and camaraderie`
      },
      {
        id: 'how-to-create-team',
        question: 'How do I create and manage a team?',
        answer: `**Creating a Team:**
1. Go to Team Dashboard
2. Click "Create Team"
3. Choose team name and description
4. Set team settings and privacy
5. Invite members

**Team Management:**
• Add/remove members
• Assign roles and permissions
• Update team information
• Manage team settings
• Track team performance

**Member Invitations:**
• Send email invitations
• Share team codes
• Manage pending invites
• Set member permissions
• Monitor member activity

**Team Settings:**
• Privacy controls
• Member limits
• Competition preferences
• Achievement tracking
• Performance analytics

**Captain Responsibilities:**
• Team leadership
• Member management
• Competition coordination
• Performance monitoring
• Team communication`
      },
      {
        id: 'team-competition',
        question: 'How do team competitions work?',
        answer: `**Competition Types:**
• Head-to-head matches
• Tournament brackets
• League competitions
• Special events
• Achievement challenges

**Scoring System:**
• Individual scores contribute to team total
• Team performance rankings
• Historical performance tracking
• Achievement points
• Competition statistics

**Match Structure:**
• Teams compete in trivia games
• Individual scores combined
• Team totals compared
• Winners determined
• Rankings updated

**Leaderboards:**
• Real-time rankings
• Historical performance
• Achievement tracking
• Competition statistics
• Team comparisons

**Rewards:**
• Achievement badges
• Team recognition
• Competition prizes
• Performance rewards
• Social status`
      }
    ],
    'security-privacy': [
      {
        id: 'account-security',
        question: 'How is my account secured?',
        answer: `**Advanced Security Features:**
• Secure authentication with Firebase
• Password protection and encryption
• Two-factor authentication (2FA)
• Session management with anti-abuse protection
• Secure data transmission (HTTPS)

**Anti-Abuse Protection:**
• Single session limit per user
• Device fingerprinting
• IP address monitoring
• Real-time abuse detection
• Automatic session termination

**Data Protection:**
• Encrypted data storage
• Secure payment processing with Stripe
• Privacy compliance (GDPR)
• Regular security updates
• Data backup systems

**Account Management:**
• Password reset capabilities
• Account recovery options
• Security settings
• Login history tracking
• Device management

**Privacy Controls:**
• Personal data protection
• Anonymous analytics
• User consent management
• Data retention policies
• Secure cookie handling

**Best Practices:**
• Use strong passwords
• Enable 2FA for enhanced security
• Log out from shared devices
• Monitor account activity
• Report suspicious activity immediately`
      },
      {
        id: 'session-management',
        question: 'How does session management work?',
        answer: `**Game-Friendly Session Management:**
• Extended timeouts during games (3 hours)
• Host sessions: 4 hours (extra protection)
• Admin sessions: 24 hours (full duration)
• Automatic activity tracking

**Session Protection:**
• Single session per user prevents account sharing
• Game sessions protected from automatic logout
• Session warnings without forced logout during games
• One-click session extension

**Activity Tracking:**
• Automatic tracking of user interactions
• Mouse, keyboard, touch, and scroll monitoring
• Game-specific activity detection
• Real-time session validation

**Security Features:**
• Device fingerprinting
• IP address monitoring
• Abuse pattern detection
• Real-time security logging
• Admin monitoring dashboard

**User Experience:**
• Legitimate users unaffected
• Clear session warnings
• Easy session extension
• No interruption during games`
      },
      {
        id: 'anti-abuse-system',
        question: 'What is the anti-abuse system?',
        answer: `**Anti-Abuse Protection:**
• Prevents account sharing between hosts
• Blocks multiple venue usage with single license
• Protects against game sharing abuse
• Ensures fair pricing model

**Key Features:**
• Single session limit per user
• Single active game per host
• Device fingerprinting
• IP address monitoring
• Real-time abuse detection

**Abuse Detection:**
• Rapid IP changes flagged
• Device fingerprint changes detected
• Multiple recent sessions monitored
• Suspicious activity patterns identified
• Automatic flagging for review

**Protection Scenarios:**
• Account sharing between hosts
• Multiple venue usage with single license
• Game sharing to avoid fees
• VPN/proxy abuse detection

**Admin Monitoring:**
• Real-time security dashboard
• Active session monitoring
• Abuse attempt logging
• Manual intervention capabilities
• Suspicious user flagging

**Legitimate Use Protection:**
• Single venue hosts unaffected
• Sequential venue hosting supported
• Multiple games per day supported
• Team events fully supported`
      },
      {
        id: 'data-privacy',
        question: 'How is my data protected?',
        answer: `**Data Protection:**
• All data encrypted in transit and at rest
• Secure Firebase hosting and database
• GDPR compliance for EU users
• Regular security audits and updates

**Personal Information:**
• Minimal data collection required
• User consent for data usage
• Anonymized analytics data
• Secure payment processing

**Game Content:**
• Personal games remain private
• Marketplace games owned by Trivia Plug
• Creator revenue data protected
• Secure content storage

**Analytics Data:**
• Aggregated and anonymized
• No personal identification
• Used for platform improvement
• User consent required

**Payment Information:**
• Stripe secure payment processing
• No credit card data stored
• Encrypted transaction data
• PCI compliance maintained

**Data Retention:**
• Clear retention policies
• User data deletion options
• Automatic cleanup procedures
• Compliance with regulations`
      }
    ],
    'venue-features': [
      {
        id: 'venue-self-reporting',
        question: 'What is the venue self-reporting system?',
        answer: `**Venue Revenue Tracking:**
• Self-reporting system for trivia night revenue
• Track food and beverage sales
• Monitor player counts and engagement
• Calculate trivia night revenue estimates

**Features:**
• Easy-to-use reporting interface
• Automatic revenue calculations
• Historical data tracking
• Performance analytics
• Business insights

**Revenue Tracking:**
• Food sales reporting
• Beverage sales reporting
• Total sales calculation
• Player count tracking
• Trivia night revenue estimation (25%)

**Analytics Integration:**
• Venue performance metrics
• Host performance tracking
• Game performance data
• Business trend analysis
• ROI calculations

**Future Features:**
• POS system integration
• Automated data collection
• Advanced analytics
• Revenue optimization tools
• Business intelligence reports`
      },
      {
        id: 'venue-analytics',
        question: 'What venue analytics are available?',
        answer: `**Revenue Analytics:**
• Total sales tracking
• Trivia night revenue estimates
• Player count analytics
• Performance trends
• Business insights

**Host Performance:**
• Host effectiveness metrics
• Game completion rates
• Player engagement levels
• Revenue generation per host
• Performance comparisons

**Game Performance:**
• Game popularity metrics
• Player satisfaction scores
• Revenue generation per game
• Completion rates
• Engagement analytics

**Business Intelligence:**
• Peak performance times
• Optimal game scheduling
• Revenue optimization
• Player retention analysis
• Competitive insights

**Reporting Features:**
• Custom date ranges
• Export capabilities
• Trend analysis
• Performance alerts
• Business recommendations`
      },
      {
        id: 'venue-coordination',
        question: 'How do venues coordinate with hosts?',
        answer: `**Host Coordination:**
• Host profile and reputation system
• Performance tracking and ratings
• Scheduling and availability management
• Communication tools

**Venue Management:**
• Host booking and scheduling
• Performance monitoring
• Revenue tracking
• Business analytics

**Quality Assurance:**
• Host performance metrics
• Player satisfaction tracking
• Revenue generation analysis
• Quality improvement recommendations

**Communication:**
• Direct messaging system
• Scheduling coordination
• Performance feedback
• Business planning tools

**Business Optimization:**
• Revenue maximization
• Player retention strategies
• Host selection optimization
• Performance improvement recommendations`
      }
    ],
    'technical': [
      {
        id: 'browser-support',
        question: 'What browsers are supported?',
        answer: `**Supported Browsers:**
• Chrome (recommended)
• Firefox
• Safari
• Edge
• Mobile browsers

**System Requirements:**
• Modern web browser
• JavaScript enabled
• Stable internet connection
• Recommended: 4GB+ RAM

**Mobile Support:**
• Responsive design
• Touch-friendly interface
• Mobile-optimized features
• Cross-platform compatibility

**Performance:**
• Fast loading times
• Smooth animations
• Real-time updates
• Efficient data handling

**Troubleshooting:**
• Clear browser cache
• Update browser version
• Check internet connection
• Disable browser extensions if needed`
      },
      {
        id: 'session-timeout',
        question: 'What happens if my session times out?',
        answer: `**Session Management:**
• Game sessions: 3-hour timeout during games
• Host sessions: 4-hour timeout
• Admin sessions: 24-hour timeout
• Automatic activity tracking

**Session Warnings:**
• 30-minute warning before timeout during games
• 10-minute warning for regular sessions
• One-click session extension
• No forced logout during active games

**Game Session Protection:**
• Games protected from session timeout
• Automatic activity tracking during games
• Session extension available
• No data loss during games

**Recovery Options:**
• Easy session extension
• Automatic reconnection
• Data preservation
• Seamless user experience

**Best Practices:**
• Stay active during games
• Use session extension when needed
• Monitor session warnings
• Report issues if they occur`
      },
      {
        id: 'support-contact',
        question: 'How do I get technical support?',
        answer: `**Support Channels:**
• Help Desk page
• Support tickets
• Email support
• FAQ documentation
• Community forums

**Support Process:**
1. Check FAQ first
2. Search existing solutions
3. Submit support ticket
4. Provide detailed information
5. Follow up as needed

**Information to Include:**
• Detailed problem description
• Steps to reproduce
• Browser and system info
• Screenshots if helpful
• Account information

**Response Times:**
• FAQ: Immediate
• Support tickets: 24-48 hours
• Urgent issues: Prioritized
• Business hours: Faster response

**Self-Service:**
• Comprehensive FAQ
• Troubleshooting guides
• Video tutorials
• Community help
• Knowledge base

**Security Issues:**
• Immediate attention for security concerns
• Dedicated security support
• Abuse reporting system
• Rapid response for critical issues`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <Header />
        <div className="text-center mb-8">
          <Logo className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Help Center & FAQ</h1>
          <p className="text-gray-400">Everything you need to know about Trivia Plug</p>
        </div>

        {/* Category Navigation */}
        <div className="mb-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category.id)}
                className={`p-4 rounded-lg border transition-all duration-200 ${
                  activeCategory === category.id
                    ? 'bg-green-600 border-green-500 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-gray-750'
                }`}
              >
                <div className="text-2xl mb-2">{category.icon}</div>
                <div className="text-sm font-medium">{category.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Content */}
        <div className="space-y-4">
          {faqData[activeCategory]?.map((item) => (
            <div key={item.id} className="bg-gray-800 rounded-lg border border-gray-700">
              <button
                onClick={() => toggleItem(item.id)}
                className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-750 transition-colors"
              >
                <h3 className="text-lg font-semibold text-white">{item.question}</h3>
                <span className="text-2xl text-gray-400">
                  {openItems[item.id] ? '−' : '+'}
                </span>
              </button>
              {openItems[item.id] && (
                <div className="px-6 pb-6">
                  <div className="text-gray-300 whitespace-pre-line leading-relaxed">
                    {item.answer}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Additional Resources */}
        <div className="mt-12 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">Additional Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl mb-2">📖</div>
              <h3 className="font-semibold text-white mb-2">Documentation</h3>
              <p className="text-gray-400 text-sm">Detailed guides and tutorials</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🎥</div>
              <h3 className="font-semibold text-white mb-2">Video Tutorials</h3>
              <p className="text-gray-400 text-sm">Step-by-step video guides</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">💬</div>
              <h3 className="font-semibold text-white mb-2">Community</h3>
              <p className="text-gray-400 text-sm">Connect with other users</p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <button
            onClick={handleBackToDashboard}
            className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded transition duration-200"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAQ; 